import { cx } from '@emotion/css'
import {
    isFeatureEnabled,
    toFeatureState,
} from '@etrigan/feature-toggles-client'
import {
    DataLayerEventName,
    RenderTargetContext,
    useFeature,
    useToggleState,
} from '@news-mono/web-common'
import {
    EventPostV4DTO,
    KilledEventPostV4DTO,
} from '@west-australian-newspapers/publication-types'
import { WebsocketServiceMessageWithPayload } from '@west-australian-newspapers/websocket-message-types'
import { nth } from 'ramda'
import React, { useContext, useEffect, useState } from 'react'
import {
    CareerOneJobsWidget,
    getSevenPlusArticleButtonLink,
    isBauhausArticle,
    mappedPublicationPropsToGa4,
    Pubfeed,
    ThemeOverrideTypes,
} from '../../..'
import { SevenPlusArticleButton } from '../../../buttons/SevenPlusArticleButton'
import { TopicList } from '../../../content/TopicList/TopicList'
import { ArticleBlockContent } from '../../../publication/ArticleBlockContent/ArticleBlockContent'
import { ArticleHero } from '../../../publication/ArticleHero/ArticleHero'
import { Byline } from '../../../publication/Byline/Byline'
import { SevenFootnote } from '../../../publication/Footnote/Footnote'
import { Headline } from '../../../publication/Headline/Headline'
import { LiveEvent } from '../../../publication/LiveEvent/LiveEvent'
import { getCanonicalLink } from '../../../templates/Publication/lib/get-canonical-link'
import { getFundingType } from '../../../templates/Publication/lib/get-funding-type'
import { PublicationProps } from '../../../templates/Publication/Publication.props'
import {
    SevenNewsStyledSharing,
    StyledArticleContent,
    StyledCell,
    StyledGrid,
    StyledNestedGrid,
    StyledStickyCell,
    StyledStickyTop,
    StyledVideoCell,
} from '../../../templates/Publication/SevenNewsPublication/SevenNewsPublication.styled'
import { ThemeOverrider } from '../../../themes/ThemeOverrider/ThemeOverrider'
import { EditorialLabel } from '../../../typography/EditorialLabel/EditorialLabel'
import { getUpdatedTime } from '../../../__helpers/get-updated-time'
import { getBauhausThemeOverride } from '../../../__styling/get-bauhaus-theme-override'
import { getEditorialType } from '../lib/get-editorial-type'
import { useIsTabletOrMobileViewport } from '../../../__helpers/use-is-tablet-or-mobile-viewport'
import TVGuideMainWidget from '../../../tv-guide-widgets/TVGuideMainWidget/TVGuideMainWidget'

export const videoContainerClassName = 'sticky-container'

export const SevenNewsPublication: React.SFC<PublicationProps> = (props) => {
    const { renderTarget } = useContext(RenderTargetContext)
    const publication = props.data
    const content = publication.content
    const canonicalLink = getCanonicalLink(publication)
    const heroImage = publication.heroImage
    const targetedContentElement =
        props.targetedContent && props.targetedContent.position === 'inline'
            ? props.targetedContent.element
            : undefined

    const storyClassification = useFeature('story-classification')

    const isTabletOrMobile = useIsTabletOrMobileViewport()

    const togglesState = useToggleState()
    const toggles = toFeatureState(togglesState)

    const isBauhaus = isBauhausArticle(publication.primaryTopic.id, toggles)

    const fundingType = getFundingType(publication, storyClassification)
    const editorialType = getEditorialType(publication, storyClassification)

    const publicationKind = publication.kind
    const isEvent = publicationKind === 'event'

    const hasLegacyLiveBlogEmbed = !!content.find(
        (block) => block.kind === 'embed' && block.link.startsWith('liveblog:'),
    )

    const [liveEventUpdated, setLiveEventUpdated] = useState(
        nth(0, isEvent ? publication.entries?.documents : [])?.lastUpdated ??
            publication.lastUpdated,
    )
    const articleAvailablePayload = mappedPublicationPropsToGa4(publication)

    useEffect(() => {
        if (articleAvailablePayload) {
            props.onEvent({
                type: DataLayerEventName.articleAvailable,
                originator: 'SevenNewsPublication',
                payload: {
                    ...articleAvailablePayload,
                    price: 0.0,
                    item_list_id: '',
                    item_list_name: '',
                    component_path: '',
                },
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const websocketSetTimestamp = (
        message: WebsocketServiceMessageWithPayload<
            EventPostV4DTO | KilledEventPostV4DTO
        >,
    ) => setLiveEventUpdated(message.payload.lastUpdated)

    const getSevenPlusArticleButtons = () => {
        const buttonData = getSevenPlusArticleButtonLink(
            publication.primaryTopic.id,
            toggles,
        )
        const isEvent = publication.kind == 'event' ? true : false

        if (!isEvent && buttonData.isEnabled && buttonData.buttonLink) {
            return (
                <SevenPlusArticleButton
                    URL={buttonData.buttonLink}
                    isV2ArticlePageEnabled={isFeatureEnabled(
                        toggles,
                        '7-news-2-article-page',
                    )}
                />
            )
        }
    }

    const articleBlockContent = (
        <ArticleBlockContent
            blocks={content}
            showInlineVideoTitles={true}
            targetedContentElement={targetedContentElement}
            inlinePositionedContent={props.inlinePositionedContent}
            onEvent={props.onEvent}
            log={props.log}
            publicationKind={publication.kind}
            publicationPathname={props.location.pathname}
            adUnitPath={props.adState.targeting.adUnitPath}
            getVideoQueue={props.getVideoQueue}
        />
    )

    const heroVideo = props.heroOptions.isVideoStickyEnabled ? (
        <React.Fragment>
            <StyledStickyTop className="sticky-top" />
            <StyledVideoCell
                className={`${videoContainerClassName} hide-print`}
            >
                <StyledStickyCell className="sticky-cell" largeGap={true}>
                    <ArticleHero
                        heroVideo={publication.heroVideo}
                        heroImage={heroImage}
                        preferredCaptionSource={'video'}
                        figcaptionRenderOptions={'hidden'}
                        heading={publication.heading}
                        onEvent={props.onEvent}
                        showVideoTitles={true}
                        autoplayNextOptionssOverrides={{
                            delayDuration: 5,
                        }}
                        isVideoStickyEnabled={true}
                        getVideoQueue={props.getVideoQueue}
                        publicationPathname={props.location.pathname}
                        adUnitPath={props.adState.targeting.adUnitPath}
                        showVideoPlaylist={true}
                    />
                </StyledStickyCell>
            </StyledVideoCell>
        </React.Fragment>
    ) : (
        <StyledCell largeGap={true}>
            <ArticleHero
                heroVideo={publication.heroVideo}
                heroImage={heroImage}
                preferredCaptionSource={'video'}
                heading={publication.heading}
                onEvent={props.onEvent}
                showVideoTitles={true}
                autoplayNextOptionssOverrides={{ delayDuration: 5 }}
                isVideoStickyEnabled={false}
                getVideoQueue={props.getVideoQueue}
                publicationPathname={props.location.pathname}
                adUnitPath={props.adState.targeting.adUnitPath}
                showVideoPlaylist={true}
            />
        </StyledCell>
    )

    const articleHeader = (
        <>
            {editorialType && (
                <StyledCell>
                    <EditorialLabel type={editorialType} isLarge />
                </StyledCell>
            )}

            <StyledCell>
                <Headline text={publication.heading} />
            </StyledCell>

            <StyledCell>
                <Byline
                    profile={publication.profile}
                    text={publication.byline}
                    timestamp={publication.publicationDate}
                    timestampUpdated={
                        isEvent || hasLegacyLiveBlogEmbed
                            ? liveEventUpdated
                            : getUpdatedTime(
                                  publication.publicationDate,
                                  publication.lastUpdated,
                              )
                    }
                    fundingType={fundingType}
                    source={publication.source}
                    isFullWidth={true}
                    url={publication._self}
                    title={publication.heading}
                    requiredAccess={publication.requiredAccess}
                    allowCommenting={false}
                />
            </StyledCell>

            {heroVideo}
        </>
    )

    const renderCanoncialLink = canonicalLink && (
        <StyledCell largeGap={false}>
            <SevenFootnote
                text={[
                    'This article was originally published from ',
                    ' and appears with permission.',
                ]}
                link={canonicalLink}
                source={publication.source}
            />
        </StyledCell>
    )

    const linkbyPubfeedTopics = ['lifestyle/sponsored']

    const isPrimaryTopicIncluded =
        publication.primaryTopic &&
        linkbyPubfeedTopics.includes(publication.primaryTopic.id)

    const isSecondaryTopicSponsored =
        publication.secondaryTopics &&
        publication.secondaryTopics.some(
            (topic) => topic.id === 'lifestyle/sponsored',
        )

    const renderLinkbyPubfeed = (isPrimaryTopicIncluded ||
        isSecondaryTopicSponsored) && (
        <StyledCell largeGap={true}>
            <Pubfeed />
        </StyledCell>
    )

    const showTvGuideWidget =
        isFeatureEnabled(toggles, '7-news-tv-guide-widgets') &&
        isTabletOrMobile &&
        publication.primaryTopic.id.includes('entertainment/tv')

    return (
        <StyledGrid>
            {!isBauhaus && articleHeader}
            {isBauhaus ? (
                <StyledCell largeGap={true}>
                    <div>
                        {content && (
                            <StyledArticleContent>
                                <ThemeOverrider
                                    override={
                                        getBauhausThemeOverride(
                                            publication.primaryTopic.id,
                                        ) as ThemeOverrideTypes
                                    }
                                >
                                    {articleBlockContent}
                                </ThemeOverrider>
                                {renderCanoncialLink}
                                {getSevenPlusArticleButtons()}
                            </StyledArticleContent>
                        )}
                        {(isEvent || hasLegacyLiveBlogEmbed) && (
                            <LiveEvent
                                publication={publication}
                                log={props.log}
                                adState={props.adState}
                                onEvent={props.onEvent}
                                onWebsocketEvent={websocketSetTimestamp}
                            />
                        )}
                    </div>
                </StyledCell>
            ) : (
                <StyledCell largeGap={true}>
                    <StyledNestedGrid>
                        <aside className={cx('hide-print')}>
                            <SevenNewsStyledSharing
                                publicationId={publication.id}
                                url={publication._self}
                                text={publication.heading}
                                onEvent={props.onEvent}
                                shareOptions={['facebook', 'twitter', 'email']}
                            />
                        </aside>
                        <div>
                            {content && (
                                <StyledArticleContent>
                                    {articleBlockContent}
                                </StyledArticleContent>
                            )}
                            {renderCanoncialLink}
                        </div>
                    </StyledNestedGrid>
                </StyledCell>
            )}

            {isFeatureEnabled(toggles, '7-news-linkby-pubfeed') &&
                renderLinkbyPubfeed}

            <StyledCell>
                {isBauhaus ? (
                    <ThemeOverrider
                        override={
                            getBauhausThemeOverride(
                                publication.primaryTopic.id,
                            ) as ThemeOverrideTypes
                        }
                    >
                        <TopicList
                            primaryTopic={publication.primaryTopic}
                            secondaryTopics={publication.secondaryTopics}
                        />
                    </ThemeOverrider>
                ) : (
                    <>
                        <TopicList
                            primaryTopic={publication.primaryTopic}
                            secondaryTopics={publication.secondaryTopics}
                        />
                    </>
                )}
            </StyledCell>

            {showTvGuideWidget && <TVGuideMainWidget />}

            {isFeatureEnabled(toggles, '7-news-careerone-widget-integration') &&
            publication.primaryTopic.id.includes('business') ? (
                <>
                    <CareerOneJobsWidget
                        type={'tablet'}
                        topic={publication.primaryTopic}
                    />
                    <CareerOneJobsWidget
                        type={'mobile'}
                        topic={publication.primaryTopic}
                    />
                </>
            ) : null}
        </StyledGrid>
    )
}
SevenNewsPublication.displayName = 'SevenNewsPublication'
